import type { TiebreakerTeam, GameResult, GameResultsLookup, StepContext } from './types';
import { RankMethod } from '../enums/rank-method';

// ─── Pure arithmetic ────────────────────────────────────────────────────────

export function calcPct(wlt: number[]): number {
  const [w, l, t] = wlt;
  const denom = w + l + t;
  if (denom === 0) return 0;
  return (w + 0.5 * t) / denom;
}

export function calcPctNaN(wlt: number[]): number {
  const [w, l, t] = wlt;
  const denom = w + l + t;
  if (denom === 0) return -1;
  return (w + 0.5 * t) / denom;
}

export function calcGamesBack(topW: number, topL: number, teamW: number, teamL: number): number {
  return ((topW - teamW) + (teamL - topL)) / 2;
}

// ─── Group-and-pick ─────────────────────────────────────────────────────────

export function groupByDescending<T>(items: T[], keyFn: (t: T) => number): T[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => keyFn(b) - keyFn(a));
  const groups: T[][] = [];
  let currentKey = keyFn(sorted[0]);
  let currentGroup: T[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const key = keyFn(sorted[i]);
    if (key === currentKey) {
      currentGroup.push(sorted[i]);
    } else {
      groups.push(currentGroup);
      currentGroup = [sorted[i]];
      currentKey = key;
    }
  }
  groups.push(currentGroup);
  return groups;
}

export function bestOf<T>(teams: T[], keyFn: (t: T) => number): T[] {
  return groupByDescending(teams, keyFn)[0] ?? [];
}

export function worstOf<T>(teams: T[], keyFn: (t: T) => number): T[] {
  const groups = groupByDescending(teams, keyFn);
  return groups[groups.length - 1] ?? [];
}

// ─── Head-to-head computation ───────────────────────────────────────────────

export function computeHthRecords(
  teams: TiebreakerTeam[],
  games: GameResultsLookup,
  gameFilter?: (game: GameResult) => boolean,
): Map<string, Map<string, number[]>> {
  const records = new Map<string, Map<string, number[]>>();
  for (const team of teams) {
    records.set(team.teamAbbreviation, new Map());
  }
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) {
      const t1 = teams[i].teamAbbreviation;
      const t2 = teams[j].teamAbbreviation;
      let matchups = games.getGamesBetween(t1, t2);
      if (gameFilter) matchups = matchups.filter(gameFilter);
      const r1 = [0, 0, 0];
      const r2 = [0, 0, 0];
      for (const g of matchups) {
        if (g.winner === t1) { r1[0]++; r2[1]++; }
        else if (g.winner === t2) { r2[0]++; r1[1]++; }
      }
      records.get(t1)!.set(t2, r1);
      records.get(t2)!.set(t1, r2);
    }
  }
  return records;
}

export function hthWinPct(
  hthRecords: Map<string, Map<string, number[]>>,
  team: string,
): number {
  const opponents = hthRecords.get(team);
  if (!opponents) return -1;
  let totalW = 0, totalL = 0, totalT = 0;
  for (const [, record] of opponents) {
    totalW += record[0];
    totalL += record[1];
    totalT += record[2];
  }
  return calcPctNaN([totalW, totalL, totalT]);
}

export function groupByHthPct(
  teams: TiebreakerTeam[],
  games: GameResultsLookup,
): { teams: TiebreakerTeam[]; hthPct: number }[] {
  const hthRecords = computeHthRecords(teams, games);
  const teamsWithPct = teams.map(t => ({
    team: t,
    pct: hthWinPct(hthRecords, t.teamAbbreviation),
  }));
  teamsWithPct.sort((a, b) => b.pct - a.pct);
  const groups: { teams: TiebreakerTeam[]; hthPct: number }[] = [];
  let currentPct = teamsWithPct[0].pct;
  let currentGroup: TiebreakerTeam[] = [teamsWithPct[0].team];
  for (let i = 1; i < teamsWithPct.length; i++) {
    if (teamsWithPct[i].pct === currentPct) {
      currentGroup.push(teamsWithPct[i].team);
    } else {
      groups.push({ teams: currentGroup, hthPct: currentPct });
      currentGroup = [teamsWithPct[i].team];
      currentPct = teamsWithPct[i].pct;
    }
  }
  groups.push({ teams: currentGroup, hthPct: currentPct });
  return groups;
}

// ─── Sweep detection ────────────────────────────────────────────────────────

export function findSweepWinner(
  teams: TiebreakerTeam[],
  hthRecords: Map<string, Map<string, number[]>>,
): TiebreakerTeam | null {
  for (const candidate of teams) {
    const opponents = hthRecords.get(candidate.teamAbbreviation);
    if (!opponents) continue;
    let sweeps = true;
    for (const other of teams) {
      if (other === candidate) continue;
      const record = opponents.get(other.teamAbbreviation);
      if (!record) { sweeps = false; break; }
      if (calcPctNaN(record) <= 0.5) { sweeps = false; break; }
    }
    if (sweeps) return candidate;
  }
  return null;
}

// ─── Division winner filtering ──────────────────────────────────────────────

export function filterDivisionWinners<T extends TiebreakerTeam & { isDivisionWinner: boolean }>(
  teams: T[],
): { divisionWinners: T[]; nonWinners: T[] } {
  const divisionWinners: T[] = [];
  const nonWinners: T[] = [];
  for (const t of teams) {
    if (t.isDivisionWinner) divisionWinners.push(t);
    else nonWinners.push(t);
  }
  return { divisionWinners, nonWinners };
}

// ─── Default coin flip ──────────────────────────────────────────────────────

export function defaultCoinFlip<T extends TiebreakerTeam>(teams: T[]): T {
  let sum = 0;
  for (let i = 0; i < teams.length; i++) {
    sum += Math.random() * Math.pow(2, 8);
  }
  return teams[Math.floor(sum % teams.length)];
}

// ─── Explanation helpers ────────────────────────────────────────────────────

export function addExplanation<T extends TiebreakerTeam>(
  ctx: StepContext<T>,
  team: TiebreakerTeam,
  message: string,
): void {
  const list = ctx.explanations.get(team.teamAbbreviation);
  if (list) list.push(message);
  else ctx.explanations.set(team.teamAbbreviation, [message]);
}

export function addExplanationAll<T extends TiebreakerTeam>(
  ctx: StepContext<T>,
  teams: TiebreakerTeam[],
  message: string,
): void {
  for (const t of teams) addExplanation(ctx, t, message);
}
